import { memo, useState, type ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import BibleVerse from './BibleVerse'
import ErrorBoundary from './ErrorBoundary'
import { detectBibleReferences } from '../lib/bibleRefs'

interface Props {
  role: 'user' | 'assistant'
  content: string
  model?: string
  streaming?: boolean
  onRegenerate?: () => void
  isHighlighted?: boolean
  onHighlight?: (selectedText: string) => void
}

function renderWithBibleRefs(text: string): ReactNode[] {
  const refs = detectBibleReferences(text)
  if (refs.length === 0) return [text]

  const parts: ReactNode[] = []
  let lastEnd = 0

  for (const ref of refs) {
    if (ref.index > lastEnd) {
      parts.push(text.slice(lastEnd, ref.index))
    }
    parts.push(
      <ErrorBoundary key={`${ref.reference}-${ref.index}`} fallback={<span>{ref.reference}</span>}>
        <BibleVerse reference={ref.reference} />
      </ErrorBoundary>
    )
    lastEnd = ref.index + ref.length
  }

  if (lastEnd < text.length) {
    parts.push(text.slice(lastEnd))
  }

  return parts
}

function processChildren(children: ReactNode): ReactNode {
  if (typeof children === 'string') {
    return renderWithBibleRefs(children)
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === 'string') {
        const parts = renderWithBibleRefs(child)
        return parts.length === 1 && typeof parts[0] === 'string' ? child : <span key={i}>{parts}</span>
      }
      return child
    })
  }
  return children
}

const MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: ReactNode }) => <p>{processChildren(children)}</p>,
  li: ({ children }: { children?: ReactNode }) => <li>{processChildren(children)}</li>,
  strong: ({ children }: { children?: ReactNode }) => <strong>{processChildren(children)}</strong>,
  em: ({ children }: { children?: ReactNode }) => <em>{processChildren(children)}</em>,
}

export default memo(function MessageBubble({ role, content, model, streaming, onRegenerate, isHighlighted, onHighlight }: Props) {
  const isUser = role === 'user'
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="w-[85%]">
          <div className="rounded-2xl rounded-br-md bg-user-bubble text-text-primary px-4 py-3">
            <p data-ph-mask className="whitespace-pre-wrap text-[17px] leading-relaxed">{renderWithBibleRefs(content)}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-3">
      <div className="border-t border-elevated/40 pt-3">
        {model && (
          <div className="text-xs text-text-muted mb-1.5">{model}</div>
        )}
        <div data-ph-mask className="prose prose-invert prose-lg max-w-none leading-7 text-[17px] [&_p]:my-3 [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1.5 [&_pre]:bg-bg [&_pre]:p-3 [&_pre]:rounded-lg [&_code]:text-accent [&_a]:text-accent [&_blockquote]:border-l-2 [&_blockquote]:border-accent [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-text-secondary [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1">
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={MARKDOWN_COMPONENTS}
          >
            {content}
          </Markdown>
        </div>
        {streaming && (
          <span className="inline-block w-2 h-4 bg-accent animate-pulse ml-0.5" />
        )}

        {/* Post-response action buttons */}
        {!streaming && content && (
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-text-muted hover:text-text-secondary transition-colors"
              aria-label="Copy response"
            >
              {copied ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-xs text-accent">Copied!</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                  </svg>
                  <span className="text-xs">Copy</span>
                </>
              )}
            </button>

            {onRegenerate && (
              <button
                onClick={onRegenerate}
                className="flex items-center gap-1 text-text-muted hover:text-text-secondary transition-colors"
                aria-label="Regenerate response"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                </svg>
                <span className="text-xs">Regenerate</span>
              </button>
            )}

            {onHighlight && (
              <button
                onClick={() => {
                  const sel = window.getSelection()?.toString().trim() || ''
                  onHighlight(sel)
                }}
                className={`flex items-center gap-1 transition-colors ${
                  isHighlighted
                    ? 'text-yellow-400'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
                aria-label={isHighlighted ? 'Highlighted' : 'Highlight'}
              >
                <svg className="w-3.5 h-3.5" fill={isHighlighted ? 'currentColor' : 'none'} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
                <span className="text-xs">{isHighlighted ? 'Saved' : 'Save'}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}, (prev, next) =>
  prev.content === next.content &&
  prev.role === next.role &&
  prev.model === next.model &&
  prev.streaming === next.streaming &&
  prev.isHighlighted === next.isHighlighted
)
